import { useState, useEffect } from "react";
import { Radar, Search, CheckCircle2, Link2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AdminProspector from "./AdminProspector";
import OutreachEngine from "./OutreachEngine";

function ProspectorGoogleConnect() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/calendar/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ configured: false, connected: false, gmail_connected: false }));
  useEffect(() => { load(); }, []);

  // Surface OAuth results returned via the callback redirect (?calendar_...).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("calendar_connected") === "1") {
      toast.success("Google connected — outreach will send from your inbox.");
      load();
    } else if (p.get("calendar_error")) {
      const err = p.get("calendar_error");
      if (err === "wrong_account") {
        toast.error(
          `You authorized ${p.get("got") || "the wrong account"}, but this must be ${p.get("expected") || "your Elevare work account"}. Click Connect again and pick your @elevaresolutions.me account.`,
          { duration: 15000 },
        );
      } else {
        toast.error(`Google connection failed: ${err}`, { duration: 12000 });
      }
    }
    if (p.get("calendar_connected") || p.get("calendar_error")) {
      ["calendar_connected", "calendar_error", "expected", "got"].forEach((k) => p.delete(k));
      const qs = p.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/calendar/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google connection.");
      setBusy(false);
    }
  };

  if (!status) return null;
  const gmailOn = status.gmail_connected;

  return (
    <div data-testid="prospector-google-connect"
      className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-5 py-3 ${gmailOn ? "border-[#4b7a3a]/30 bg-[#4b7a3a]/6" : "border-[#c9a961]/40 bg-[#c9a961]/8"}`}>
      <div className="flex items-start gap-2.5 text-[11px] text-[#1e3d3a]/75 max-w-xl">
        {gmailOn
          ? <CheckCircle2 className="h-4 w-4 text-[#4b7a3a] shrink-0 mt-0.5" />
          : <Mail className="h-4 w-4 text-[#a3823f] shrink-0 mt-0.5" />}
        {gmailOn ? (
          <span data-testid="prospector-google-connected">
            Google account connected — sending outreach from <span className="font-semibold text-[#4d5a24]">{status.email}</span> with Gmail + Calendar access.
          </span>
        ) : status.connected ? (
          <span>
            <span className="font-semibold text-[#a3823f]">{status.email}</span> is connected for Calendar only — re-authorize to grant <span className="font-semibold">Gmail</span> so the engine can send outreach from your inbox and auto-detect replies. Keep every permission ticked.
          </span>
        ) : status.configured ? (
          <span>Connect your Google Workspace account so the Outreach Engine can send from your inbox and detect replies. You'll authorize once (Calendar + Gmail).</span>
        ) : (
          <span className="text-[#a3823f]">Google isn't configured yet — add GOOGLE_CLIENT_ID &amp; GOOGLE_CLIENT_SECRET in the backend.</span>
        )}
      </div>
      <button type="button" data-testid="prospector-connect-google" onClick={connect}
        disabled={busy || !status.configured}
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold transition-colors duration-300 disabled:opacity-40 ${gmailOn ? "border border-[#1e3d3a]/20 text-[#1e3d3a] hover:border-[#a3823f] hover:text-[#a3823f]" : "bg-[#1e3d3a] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a]"}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
        {gmailOn ? "Reconnect" : status.connected ? "Re-authorize with Gmail" : "Connect Google"}
      </button>
    </div>
  );
}

export default function ProspectorTab({ seed = null }) {
  const [view, setView] = useState("research");
  const [lookupPrefill, setLookupPrefill] = useState(null);

  const researchBusiness = (name, location) => {
    setLookupPrefill({ name: name || "", location: location || "", ts: Date.now() });
    setView("research");
  };

  // A client launched from the CRM Vault seeds the research/assessment view.
  useEffect(() => {
    if (seed?.ts) {
      setLookupPrefill({ name: seed.name || "", location: seed.location || "", ts: seed.ts });
      setView("research");
    }
  }, [seed?.ts]);

  // Deep-link: ?leads=<filter> opens the Outreach Engine directly.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("leads")) setView("outreach");
    } catch { /* ignore */ }
  }, []);

  return (
    <div data-testid="tab-content-prospector" className="space-y-4">
      <ProspectorGoogleConnect />
      <div className="inline-flex rounded-full border border-[#1e3d3a]/12 bg-white p-1" data-testid="prospector-subnav">
        <button type="button" data-testid="prospector-view-research" onClick={() => setView("research")}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.16em] transition-colors duration-300 ${view === "research" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#a3823f]"}`}>
          <Search className="h-3.5 w-3.5" /> Prospect Research
        </button>
        <button type="button" data-testid="prospector-view-outreach" onClick={() => setView("outreach")}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.16em] transition-colors duration-300 ${view === "outreach" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#a3823f]"}`}>
          <Radar className="h-3.5 w-3.5" /> Outreach Engine
        </button>
      </div>
      <div style={{ display: view === "research" ? "block" : "none" }}>
        <AdminProspector lookupPrefill={lookupPrefill} />
      </div>
      <div style={{ display: view === "outreach" ? "block" : "none" }}>
        <OutreachEngine onResearch={researchBusiness} />
      </div>
    </div>
  );
}
